import { useState, useEffect } from "react";
import { C, S, font, fontCond, buildPyramidRows, GENDER, genderColor } from "./constants";
import { useAppState } from "./hooks/useAppState";
import { Avatar, YellowTriangle, ClubIcon, ClubLogo } from "./components/ui";
import { shortName, canChallenge, getBlockStatus, getChallengeStatus, isChallengerBlocked, getPlayerStats, isExpired, fmtDate } from "./lib/helpers";
import { RoundTimePicker } from "./lib/courtAvailability";

import LoginScreen from "./screens/LoginScreen";
import AdminPanel from "./screens/AdminPanel";
import RankingScreen from "./screens/RankingScreen";
import PyramidScreen from "./screens/PyramidScreen";
import AgendaScreen from "./screens/AgendaScreen";
import { ResultadosScreen, StatsScreen, RegrasScreen } from "./screens/OtherScreens";
import MinhaScreen from "./screens/MinhaScreen";
import SeasonsScreen from "./screens/SeasonsScreen";

// ── Responsive hook ────────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── Gender Toggle ──────────────────────────────────────────────────────────────
function GenderToggle({ gender, setGender, isMobile }) {
  return (
    <div style={{ ...S.genderToggle, marginLeft: isMobile ? 0 : 8, width: isMobile ? "100%" : undefined }}>
      {GENDER.keys.map(g => (
        <button
          key={g}
          style={{ ...S.genderBtn(gender === g, g), flex: isMobile ? 1 : undefined, fontSize: isMobile ? 14 : 13, padding: isMobile ? "10px 0" : "7px 14px" }}
          onClick={() => setGender(g)}
        >
          {GENDER.emoji[g]} {GENDER.label[g]}
        </button>
      ))}
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────────
export default function App() {
  const isMobile = useIsMobile();
  const appState = useAppState();
  const {
    state, screen, currentUser, tab, msg, msgType,
    gender, setGender,
    loginUser, loginPass, loginError, loginSugg,
    setLoginUser, setLoginPass, setLoginSugg, setScreen, setTab,
    hoveredCell, setHoveredCell, hoveredNav, setHoveredNav,
    mobileMenuOpen, setMobileMenuOpen,
    pyramidModal, setPyramidModal, modalDates, setModalDates,
    confirmModal, setConfirmModal, pickedDate, setPickedDate,
    passModal, setPassModal, profileModal, setProfileModal,
    profileEmail, setProfileEmail, profilePhone, setProfilePhone,
    statsModal, setStatsModal,
    counterModal, setCounterModal, counterDates, setCounterDates,
    refuseModal, setRefuseModal,
    mediationModal, setMediationModal,
    mediationSchedule, setMediationSchedule, mediationDate, setMediationDate,
    pyramidTooltip, setPyramidTooltip,
    oldPass, setOldPass, newPass1, setNewPass1, newPass2, setNewPass2,
    challengeTarget, setChallengeTarget, challengeDates, setChallengeDates,
    rankSearch, setRankSearch,
    agendaView, setAgendaView, calendarDate, setCalendarDate,
    resultConfirmModal, setResultConfirmModal, requestResultConfirm,
    photoUploading,
    editPlayerModal, setEditPlayerModal,
    editPlayerName, setEditPlayerName,
    editPlayerEmail, setEditPlayerEmail,
    editPlayerPhone, setEditPlayerPhone,
    adminTab, setAdminTab,
    newPlayerName, setNewPlayerName,
    removePlayerId, setRemovePlayerId, removeConfirm, setRemoveConfirm,
    resetPlayerId, setResetPlayerId, resetNewPass, setResetNewPass,
    adjustPlayerId, setAdjustPlayerId, adjustPosition, setAdjustPosition,
    adminChrID, setAdminChrID, adminChdID, setAdminChdID,
    adminChalDate, setAdminChalDate,
    cancelChalId, setCancelChalId, cancelChalConfirm, setCancelChalConfirm,
    resultChalId, setResultChalId, resultWinner, setResultWinner,
    resultType, setResultType, resultSets, setResultSets,
    inactivePlayer, setInactivePlayer, inactiveSearch, setInactiveSearch,
    inactiveSearchOpen, setInactiveSearchOpen, inactiveDays, setInactiveDays,
    inactivityHistory, loadInactivityHistory, histLoading,
    histPlayerSearch, setHistPlayerSearch, histPlayerSearchOpen, setHistPlayerSearchOpen,
    histPlayerId, setHistPlayerId,
    histSince, setHistSince, histUntil, setHistUntil, histPenalty, setHistPenalty,
    addHistoricalInactivity,
    editingHistId, setEditingHistId,
    editHistSince, setEditHistSince, editHistUntil, setEditHistUntil,
    editHistPenalty, setEditHistPenalty,
    updateHistoricalInactivity, deleteHistoricalInactivity, migrateInactivityHistory,
    editChalId, setEditChalId, editChalDate, setEditChalDate,
    editResultId, setEditResultId, editResultWinner, setEditResultWinner,
    editResultType, setEditResultType, editResultSets, setEditResultSets,
    editingRule, setEditingRule, editRuleIcon, setEditRuleIcon,
    editRuleTitle, setEditRuleTitle, editRuleText, setEditRuleText,
    propAsChrID, setPropAsChrID, propAsChdID, setPropAsChdID,
    propAsDates, setPropAsDates,
    blockDate, setBlockDate, blockAllDay, setBlockAllDay,
    blockHours, setBlockHours,
    holidayDate, setHolidayDate, holidayName, setHolidayName,
    addHoliday, removeHoliday,
    slotDayType, setSlotDayType, slotHour, setSlotHour, slotMaxCourts, setSlotMaxCourts,
    addCourtSlot, updateCourtSlot, removeCourtSlot,
    periodDateFrom, setPeriodDateFrom, periodDateTo, setPeriodDateTo,
    periodAllDay, setPeriodAllDay, periodHours, setPeriodHours,
    periodLabel, setPeriodLabel,
    addBlockedPeriod, removeBlockedPeriod,
    resultMatchDate, setResultMatchDate,
    me, flash, withDB,
    allChallenges,
    handleLoginInput, login, logout,
    changePassword, uploadPhoto,
    proposeChallenge, proposeFromPyramid,
    confirmChallengeDate, refuseChallenge, requestMediation,
    counterProposeDates, cancelChallengeByChallenger, markAllRead,
    addPlayer, removePlayer, resetPassword, adjustPlayerPosition,
    cancelChallenge, adminProposeChallenge, adminProposeAsPlayer, editChallengeDate,
    registerResult, saveEditResult, deleteResult,
    setInactivity, removeInactivity, unblockPlayer,
    saveProfile, saveEditPlayer, saveRule,
    addBlockedDate, removeBlockedDate,
    save,
  } = appState;

  // ── Loading ────────────────────────────────────────────────────────────────
  if (!state) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: C.bg, color: C.text, fontFamily: font }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎾</div>
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 3, opacity: .5, fontFamily: fontCond }}>CARREGANDO…</div>
        <div style={{ marginTop: 20, width: 40, height: 4, background: C.border, borderRadius: 2, overflow: "hidden", margin: "20px auto 0" }}>
          <div style={{ height: "100%", background: C.primary, borderRadius: 2, animation: "load 1.2s ease-in-out infinite", width: "60%" }}/>
        </div>
        <style>{`@keyframes load { 0%{transform:translateX(-100%)} 100%{transform:translateX(200%)} }`}</style>
      </div>
    </div>
  );

  // ── Login screen ───────────────────────────────────────────────────────────
  if (screen === "login") return (
    <LoginScreen
      isMobile={isMobile}
      loginUser={loginUser} loginPass={loginPass}
      loginError={loginError} loginSugg={loginSugg}
      msg={msg} msgType={msgType}
      setLoginUser={setLoginUser} setLoginPass={setLoginPass}
      setLoginSugg={setLoginSugg} setScreen={setScreen} setTab={setTab}
      handleLoginInput={handleLoginInput} login={login}
      gender={gender} setGender={setGender}
    />
  );

  // ── Derived values ─────────────────────────────────────────────────────────
  const sorted        = [...state.players].sort((a, b) => a.position - b.position);
  const PYRAMID_ROWS  = buildPyramidRows(sorted.length);
  const maxRowsUp     = sorted.length >= 91 ? 2 : 1;
  const myChallenges  = me ? state.challenges.filter(c => c.challengerId === me.id || c.challengedId === me.id) : [];
  const myResults     = me ? state.results.filter(r => r.challengerName === me.name || r.challengedName === me.name) : [];
  const activeAsChallenger = me ? state.challenges.filter(c => c.challengerId === me.id).length : 0;
  const eligibleTargets = me ? sorted.filter(p =>
    canChallenge(me.position, p.position, PYRAMID_ROWS, maxRowsUp) &&
    !getBlockStatus(p) && p.id !== me.id &&
    state.challenges.filter(c => c.challengedId === p.id).length === 0
  ) : [];
  const myNotifs      = me ? (state.notifications || []).filter(n => n.playerId === me.id && n.gender === gender).sort((a, b) => new Date(b.date) - new Date(a.date)) : [];
  const unreadCount   = myNotifs.filter(n => !n.read).length;
  const pendingConfirm = me ? state.challenges.filter(c => c.challengedId === me.id && c.status === "aguardando_confirmacao" && !isExpired(c) && c.lastProposedBy === "challenger") : [];
  const pendingChallengerConfirm = me ? state.challenges.filter(c => c.challengerId === me.id && c.status === "aguardando_confirmacao" && !isExpired(c) && c.lastProposedBy === "challenged") : [];
  const pyramidData   = PYRAMID_ROWS.map(([a, b]) =>
    sorted.filter(p => p.position >= a && p.position <= b).map(p => {
      const _bs = getBlockStatus(p), isMe = me ? p.id === me.id : false;
      const cs = getChallengeStatus(p, state.challenges);
      const bs = _bs || ((cs === "desafiado" || cs === "ambos") ? "bloqueado" : null);
      const eligible = (me && activeAsChallenger === 0 && !isChallengerBlocked(me))
        ? (canChallenge(me.position, p.position, PYRAMID_ROWS, maxRowsUp) && bs !== "inativo" && bs !== "bloqueado" && state.challenges.filter(c => c.challengedId === p.id).length === 0)
        : false;
      return { p, bs, isMe, eligible };
    })
  );
  const isPublic = screen === "public";

  // cor primária do contexto atual
  const primaryColor = genderColor(gender);

  // ── Tabs config ────────────────────────────────────────────────────────────
  const tabs = screen === "admin"
    ? [["agenda","Agenda","📅"],["resultados","Resultados","📋"],["ranking","Ranking","🏅"],["piramide","Pirâmide","△"],["stats","Stats","📊"],["temporadas","Temporadas","🗂️"],["admin","Admin","⚙️"]]
    : screen === "public"
    ? [["ranking","Ranking","🏅"],["piramide","Pirâmide","△"],["agenda","Agenda","📅"],["resultados","Resultados","📋"],["stats","Stats","📊"],["temporadas","Temporadas","🗂️"],["regras","Regras","📖"]]
    : [["minha","Minha Área","👤"],["ranking","Ranking","🏅"],["piramide","Pirâmide","△"],["agenda","Agenda","📅"],["resultados","Resultados","📋"],["stats","Stats","📊"],["temporadas","Temporadas","🗂️"],["regras","Regras","📖"]];

  return (
    <div style={S.app}>
      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <div style={{ ...S.header(isMobile), flexDirection: "column", padding: 0, height: "auto" }}>

        {/* ── Linha 1: Logo + Gender (centro) + Usuário/Sair ── */}
        <div style={{ display: "flex", alignItems: "center", width: "100%", padding: isMobile ? "0 12px" : "0 24px", height: 52, boxSizing: "border-box", position: "relative" }}>

          {/* Logo — esquerda */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {isMobile ? <ClubIcon size={34}/> : <ClubLogo height={32}/>}
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, color: "#fff", background: C.primary, borderRadius: 6, padding: "2px 7px", marginTop: 2 }}>BETA</span>
          </div>

          {/* Toggle M/F — centro absoluto */}
          {!isMobile && (
            <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
              <GenderToggle gender={gender} setGender={setGender} isMobile={false}/>
            </div>
          )}

          {/* Spacer */}
          <div style={{ flex: 1 }}/>

          {/* Usuário + Sair — extrema direita */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {me && !isMobile && (
              <button
                onClick={() => { setTab("minha"); markAllRead(me.id); }}
                style={{ display: "flex", alignItems: "center", gap: 8, background: tab === "minha" ? primaryColor : C.cardAlt, color: tab === "minha" ? "#fff" : C.text, border: tab === "minha" ? "none" : "1px solid " + C.border, borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: font, minHeight: 36, position: "relative" }}>
                <Avatar player={me} size={22}/>
                <span>{shortName(me.name)}</span>
                {unreadCount > 0 && <span style={{ background: primaryColor, color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>{unreadCount}</span>}
              </button>
            )}
            {isMobile && (
              <button
                onClick={() => setMobileMenuOpen(o => !o)}
                style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 8, width: 40, height: 40, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, cursor: "pointer", padding: 0, position: "relative" }}
                aria-label="Menu">
                {me && unreadCount > 0 && <span style={{ position: "absolute", top: 6, right: 6, background: primaryColor, width: 8, height: 8, borderRadius: "50%", display: "block" }}/>}
                {[0,1,2].map(i => <span key={i} style={{ display: "block", width: 18, height: 2, background: mobileMenuOpen ? primaryColor : C.muted, borderRadius: 2, transition: "background .15s" }}/>)}
              </button>
            )}
            {!isMobile && (isPublic
              ? <button style={S.logoutBtn} onClick={() => setScreen("login")}>Login</button>
              : <button style={S.logoutBtn} onClick={logout}>Sair</button>
            )}
          </div>
        </div>

        {/* ── Linha 2: Nav tabs — apenas desktop ── */}
        {!isMobile && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "0 24px", height: 40, boxSizing: "border-box", gap: 2 }}>
            {tabs.filter(([k]) => k !== "minha").map(([k, l]) => (
              <button key={k}
                style={{
                  ...S.navBtn(tab === k, hoveredNav === k),
                  fontSize: 12,
                  padding: "6px 14px",
                  minHeight: 30,
                }}
                onMouseEnter={() => setHoveredNav(k)}
                onMouseLeave={() => setHoveredNav(null)}
                onClick={() => { setTab(k); if (k === "minha" && me) markAllRead(me.id); }}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ══ MOBILE DROPDOWN MENU ════════════════════════════════════════════ */}
      {isMobile && mobileMenuOpen && (
        <>
          <div onClick={() => setMobileMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 150, background: "rgba(0,0,0,.35)", backdropFilter: "blur(2px)" }}/>
          <div style={{ position: "fixed", top: 52, right: 0, zIndex: 151, background: C.card, borderLeft: "1px solid " + C.border, borderBottom: "1px solid " + C.border, borderRadius: "0 0 0 16px", width: "75vw", maxWidth: 300, minWidth: 260, boxShadow: "-4px 8px 32px rgba(0,0,0,.18)", overflow: "hidden" }}>
            {me && (
              <div style={{ padding: "14px 16px", borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 12, background: C.cardAlt }}>
                <Avatar player={me} size={38}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{me.name}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Posição #{me.position}</div>
                </div>
                {unreadCount > 0 && <span style={{ background: primaryColor, color: "#fff", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{unreadCount}</span>}
              </div>
            )}

            {/* Seletor M/F — mobile: dentro do menu */}
            <div style={{ padding: "10px 16px", borderBottom: "1px solid " + C.border, background: C.cardAlt }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>Ranking</div>
              <GenderToggle gender={gender} setGender={(g) => { setGender(g); setMobileMenuOpen(false); }} isMobile={true}/>
            </div>

            {tabs.map(([k, l, icon]) => {
              const isActive = tab === k, isMinha = k === "minha";
              return (
                <button key={k}
                  onClick={() => { setTab(k); if (isMinha && me) markAllRead(me.id); setMobileMenuOpen(false); }}
                  style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 18px", background: isActive ? primaryColor + "12" : "transparent", border: "none", borderBottom: "1px solid " + C.border + "66", cursor: "pointer", fontFamily: font, textAlign: "left", color: isActive ? primaryColor : C.text, fontWeight: isActive ? 700 : 500, fontSize: 15, WebkitTapHighlightColor: "transparent" }}>
                  <span style={{ fontSize: 20, width: 28, textAlign: "center", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon === "△" ? <YellowTriangle size={20}/> : icon}</span>
                  <span style={{ flex: 1 }}>{isMinha ? "Minha Área" : l}</span>
                  {isActive && <span style={{ width: 6, height: 6, borderRadius: "50%", background: primaryColor, flexShrink: 0 }}/>}
                  {isMinha && me && unreadCount > 0 && !isActive && <span style={{ background: primaryColor, color: "#fff", borderRadius: 20, padding: "2px 7px", fontSize: 11, fontWeight: 700 }}>{unreadCount}</span>}
                </button>
              );
            })}
            <div style={{ padding: "12px 16px", borderTop: "1px solid " + C.border, display: "flex", gap: 8 }}>
              {isPublic
                ? <button style={{ ...S.btn(primaryColor), flex: 1, fontSize: 13, padding: "10px" }} onClick={() => { setScreen("login"); setMobileMenuOpen(false); }}>Entrar</button>
                : <button style={{ ...S.btn(C.muted, true), flex: 1, fontSize: 13, padding: "10px" }} onClick={() => { logout(); setMobileMenuOpen(false); }}>Sair</button>
              }
            </div>
          </div>
        </>
      )}

      {/* ══ BODY ════════════════════════════════════════════════════════════ */}
      <div style={S.body(isMobile)}>
        {msg && <div style={S.msg(msgType)}>{msg}</div>}

        {me && (pendingConfirm.length > 0 || pendingChallengerConfirm.length > 0) && tab !== "minha" && (
          <div style={{ background: "#fff8ec", border: "1px solid " + C.warning + "55", borderRadius: 10, padding: "12px 14px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontSize: 14, color: C.warning, fontWeight: 600 }}>⚠️ {pendingConfirm.length + pendingChallengerConfirm.length} desafio(s) pendente(s)!</span>
            <button style={{ ...S.btn(C.warning), padding: "8px 14px", fontSize: 13, minHeight: 36 }} onClick={() => setTab("minha")}>Ver</button>
          </div>
        )}

        {/* ══ TABS ══════════════════════════════════════════════════════════ */}
        {tab === "ranking" && (
          <RankingScreen
            sorted={sorted} me={me} state={state} isPublic={isPublic}
            rankSearch={rankSearch} setRankSearch={setRankSearch}
            setStatsModal={setStatsModal} isMobile={isMobile}
          />
        )}

        {tab === "piramide" && (
          <PyramidScreen
            pyramidData={pyramidData} PYRAMID_ROWS={PYRAMID_ROWS} sorted={sorted}
            me={me} isPublic={isPublic} state={state}
            hoveredCell={hoveredCell} setHoveredCell={setHoveredCell}
            pyramidModal={pyramidModal} setPyramidModal={setPyramidModal}
            modalDates={modalDates} setModalDates={setModalDates}
            pyramidTooltip={pyramidTooltip} setPyramidTooltip={setPyramidTooltip}
            proposeFromPyramid={proposeFromPyramid}
            isMobile={isMobile} allChallenges={allChallenges}
          />
        )}

        {tab === "agenda" && (
          <AgendaScreen
            state={state} me={me} screen={screen}
            agendaView={agendaView} setAgendaView={setAgendaView}
            calendarDate={calendarDate} setCalendarDate={setCalendarDate}
            isMobile={isMobile}
          />
        )}

        {tab === "resultados" && (
          <ResultadosScreen state={state} isMobile={isMobile}/>
        )}

        {tab === "stats" && (
          <StatsScreen
            state={state} sorted={sorted}
            setStatsModal={setStatsModal} isMobile={isMobile}
          />
        )}

        {tab === "temporadas" && (
          <SeasonsScreen gender={gender} isMobile={isMobile}/>
        )}

        {tab === "regras" && (
          <RegrasScreen state={state} isMobile={isMobile}/>
        )}

        {tab === "minha" && me && (
          <MinhaScreen
            me={me} state={state} isMobile={isMobile} allChallenges={allChallenges}
            pendingConfirm={pendingConfirm} pendingChallengerConfirm={pendingChallengerConfirm}
            myChallenges={myChallenges} myResults={myResults}
            myNotifs={myNotifs} unreadCount={unreadCount}
            eligibleTargets={eligibleTargets} activeAsChallenger={activeAsChallenger}
            challengeTarget={challengeTarget} setChallengeTarget={setChallengeTarget}
            challengeDates={challengeDates} setChallengeDates={setChallengeDates}
            setProfileEmail={setProfileEmail} setProfilePhone={setProfilePhone}
            setProfileModal={setProfileModal}
            setOldPass={setOldPass} setNewPass1={setNewPass1} setNewPass2={setNewPass2}
            setConfirmModal={setConfirmModal} setPickedDate={setPickedDate}
            setCounterModal={setCounterModal} setCounterDates={setCounterDates}
            setRefuseModal={setRefuseModal} setMediationModal={setMediationModal}
            proposeChallenge={proposeChallenge}
            cancelChallengeByChallenger={cancelChallengeByChallenger}
            inactivityHistory={inactivityHistory}
            currentUser={currentUser}
            gender={gender}
          />
        )}

        {tab === "admin" && screen === "admin" && (
          <AdminPanel
            isMobile={isMobile} state={state} sorted={sorted} gender={gender} allChallenges={allChallenges}
            adminTab={adminTab} setAdminTab={setAdminTab}
            adminChrID={adminChrID} setAdminChrID={setAdminChrID}
            adminChdID={adminChdID} setAdminChdID={setAdminChdID}
            adminChalDate={adminChalDate} setAdminChalDate={setAdminChalDate}
            adminProposeChallenge={adminProposeChallenge}
            propAsChrID={propAsChrID} setPropAsChrID={setPropAsChrID}
            propAsChdID={propAsChdID} setPropAsChdID={setPropAsChdID}
            propAsDates={propAsDates} setPropAsDates={setPropAsDates}
            adminProposeAsPlayer={adminProposeAsPlayer}
            cancelChalId={cancelChalId} setCancelChalId={setCancelChalId}
            cancelChalConfirm={cancelChalConfirm} setCancelChalConfirm={setCancelChalConfirm}
            cancelChallenge={cancelChallenge}
            editChalId={editChalId} setEditChalId={setEditChalId}
            editChalDate={editChalDate} setEditChalDate={setEditChalDate}
            editChallengeDate={editChallengeDate}
            mediationSchedule={mediationSchedule} setMediationSchedule={setMediationSchedule}
            mediationDate={mediationDate} setMediationDate={setMediationDate}
            resultChalId={resultChalId} setResultChalId={setResultChalId}
            resultWinner={resultWinner} setResultWinner={setResultWinner}
            resultType={resultType} setResultType={setResultType}
            resultSets={resultSets} setResultSets={setResultSets}
            resultMatchDate={resultMatchDate} setResultMatchDate={setResultMatchDate}
            requestResultConfirm={requestResultConfirm}
            registerResult={registerResult}
            resultConfirmModal={resultConfirmModal} setResultConfirmModal={setResultConfirmModal}
            editResultId={editResultId} setEditResultId={setEditResultId}
            editResultWinner={editResultWinner} setEditResultWinner={setEditResultWinner}
            editResultType={editResultType} setEditResultType={setEditResultType}
            editResultSets={editResultSets} setEditResultSets={setEditResultSets}
            saveEditResult={saveEditResult}
            deleteResult={deleteResult}
            newPlayerName={newPlayerName} setNewPlayerName={setNewPlayerName}
            addPlayer={addPlayer}
            removePlayerId={removePlayerId} setRemovePlayerId={setRemovePlayerId}
            removeConfirm={removeConfirm} setRemoveConfirm={setRemoveConfirm}
            removePlayer={removePlayer}
            resetPlayerId={resetPlayerId} setResetPlayerId={setResetPlayerId}
            resetNewPass={resetNewPass} setResetNewPass={setResetNewPass}
            resetPassword={resetPassword}
            adjustPlayerId={adjustPlayerId} setAdjustPlayerId={setAdjustPlayerId}
            adjustPosition={adjustPosition} setAdjustPosition={setAdjustPosition}
            adjustPlayerPosition={adjustPlayerPosition}
            editPlayerModal={editPlayerModal} setEditPlayerModal={setEditPlayerModal}
            editPlayerName={editPlayerName} setEditPlayerName={setEditPlayerName}
            editPlayerEmail={editPlayerEmail} setEditPlayerEmail={setEditPlayerEmail}
            editPlayerPhone={editPlayerPhone} setEditPlayerPhone={setEditPlayerPhone}
            saveEditPlayer={saveEditPlayer}
            inactivePlayer={inactivePlayer} setInactivePlayer={setInactivePlayer}
            inactiveSearch={inactiveSearch} setInactiveSearch={setInactiveSearch}
            inactiveSearchOpen={inactiveSearchOpen} setInactiveSearchOpen={setInactiveSearchOpen}
            inactiveDays={inactiveDays} setInactiveDays={setInactiveDays}
            setInactivity={setInactivity} removeInactivity={removeInactivity}
            unblockPlayer={unblockPlayer}
            inactivityHistory={inactivityHistory} loadInactivityHistory={loadInactivityHistory} histLoading={histLoading}
            histPlayerSearch={histPlayerSearch} setHistPlayerSearch={setHistPlayerSearch}
            histPlayerSearchOpen={histPlayerSearchOpen} setHistPlayerSearchOpen={setHistPlayerSearchOpen}
            histPlayerId={histPlayerId} setHistPlayerId={setHistPlayerId}
            histSince={histSince} setHistSince={setHistSince}
            histUntil={histUntil} setHistUntil={setHistUntil}
            histPenalty={histPenalty} setHistPenalty={setHistPenalty}
            addHistoricalInactivity={addHistoricalInactivity}
            editingHistId={editingHistId} setEditingHistId={setEditingHistId}
            editHistSince={editHistSince} setEditHistSince={setEditHistSince}
            editHistUntil={editHistUntil} setEditHistUntil={setEditHistUntil}
            editHistPenalty={editHistPenalty} setEditHistPenalty={setEditHistPenalty}
            updateHistoricalInactivity={updateHistoricalInactivity}
            deleteHistoricalInactivity={deleteHistoricalInactivity}
            migrateInactivityHistory={migrateInactivityHistory}
            blockDate={blockDate} setBlockDate={setBlockDate}
            blockAllDay={blockAllDay} setBlockAllDay={setBlockAllDay}
            blockHours={blockHours} setBlockHours={setBlockHours}
            addBlockedDate={addBlockedDate} removeBlockedDate={removeBlockedDate}
            holidayDate={holidayDate} setHolidayDate={setHolidayDate}
            holidayName={holidayName} setHolidayName={setHolidayName}
            addHoliday={addHoliday} removeHoliday={removeHoliday}
            slotDayType={slotDayType} setSlotDayType={setSlotDayType}
            slotHour={slotHour} setSlotHour={setSlotHour}
            slotMaxCourts={slotMaxCourts} setSlotMaxCourts={setSlotMaxCourts}
            addCourtSlot={addCourtSlot} updateCourtSlot={updateCourtSlot} removeCourtSlot={removeCourtSlot}
            periodDateFrom={periodDateFrom} setPeriodDateFrom={setPeriodDateFrom}
            periodDateTo={periodDateTo} setPeriodDateTo={setPeriodDateTo}
            periodAllDay={periodAllDay} setPeriodAllDay={setPeriodAllDay}
            periodHours={periodHours} setPeriodHours={setPeriodHours}
            periodLabel={periodLabel} setPeriodLabel={setPeriodLabel}
            addBlockedPeriod={addBlockedPeriod} removeBlockedPeriod={removeBlockedPeriod}
            editingRule={editingRule} setEditingRule={setEditingRule}
            editRuleIcon={editRuleIcon} setEditRuleIcon={setEditRuleIcon}
            editRuleTitle={editRuleTitle} setEditRuleTitle={setEditRuleTitle}
            editRuleText={editRuleText} setEditRuleText={setEditRuleText}
            saveRule={saveRule}
            flash={flash} save={save}
            gender={gender}
          />
        )}
      </div>

      {/* ══ FOOTER ══════════════════════════════════════════════════════════ */}
      <div style={{ textAlign: "center", padding: "16px 0 20px", marginTop: 8 }}>
        <span style={{ fontSize: 11, color: C.muted }}>Desenvolvido por: </span>
        <a href="https://pgaspar.com.br/" target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 11, color: C.muted, textDecoration: "none", borderBottom: "1px solid " + C.border }}>
          P.Gaspar
        </a>
      </div>


      {/* ══ MODALS ══════════════════════════════════════════════════════════ */}

      {confirmModal && (
        <div style={{ ...S.modal, alignItems: "center" }} onClick={() => { setConfirmModal(null); setPickedDate(""); }}>
          <div style={S.modalBox(isMobile)} onClick={e => e.stopPropagation()}>
            {isMobile && <div style={{ width: 40, height: 4, background: C.border, borderRadius: 2, margin: "0 auto 20px" }}/>}
            <div style={{ fontFamily: fontCond, fontSize: 20, fontWeight: 900, color: C.text, marginBottom: 4 }}>Confirmar Data</div>
            <div style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>Data: <b style={{ color: C.accent }}>{fmtDate(pickedDate)}</b></div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={{ ...S.btn(C.success), flex: 1 }} onClick={confirmChallengeDate}>Confirmar</button>
              <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => { setConfirmModal(null); setPickedDate(""); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {refuseModal && (
        <div style={{ ...S.modal, alignItems: "center" }} onClick={() => setRefuseModal(null)}>
          <div style={{ ...S.modalBox(false), width: "100%", maxWidth: 420, borderRadius: 18 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: fontCond, fontSize: 20, fontWeight: 900, color: "#e53e3e", marginBottom: 8 }}>❌ Recusar Desafio</div>
            <div style={{ fontSize: 14, color: C.muted, marginBottom: 6 }}>Ao recusar o desafio, será aplicado <b>W.O.</b> contra você:</div>
            <div style={{ background: "#fff0f0", border: "1px solid #e53e3e33", borderRadius: 10, padding: "12px 14px", marginBottom: 18, fontSize: 13, color: "#c0392b", lineHeight: 1.7 }}>
              • Você cai <b>5 posições</b> no ranking<br/>
              • O desafiante fica livre para propor novo desafio
            </div>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, color: C.text }}>Tem certeza que deseja recusar?</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={{ ...S.btn("#e53e3e"), flex: 1 }} onClick={() => refuseChallenge(refuseModal.id)}>Sim, recusar</button>
              <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => setRefuseModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {mediationModal && (
        <div style={{ ...S.modal, alignItems: "center" }} onClick={() => setMediationModal(null)}>
          <div style={{ ...S.modalBox(false), width: "100%", maxWidth: 420, borderRadius: 18 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: fontCond, fontSize: 20, fontWeight: 900, color: "#e67e22", marginBottom: 8 }}>⚠️ Acionar Suporte</div>
            <div style={{ fontSize: 14, color: C.muted, marginBottom: 12 }}>As datas propostas não foram aceitas por nenhuma das partes.</div>
            <div style={{ background: "#fff8f0", border: "1px solid #e67e2233", borderRadius: 10, padding: "12px 14px", marginBottom: 18, fontSize: 13, color: "#b35a00", lineHeight: 1.7 }}>
              • O desafio será marcado como <b>em mediação</b><br/>
              • O admin será notificado e entrará em contato<br/>
              • Nenhuma alteração de posição será feita até resolução
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={{ ...S.btn("#e67e22"), flex: 1 }} onClick={() => requestMediation(mediationModal.id)}>Confirmar</button>
              <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => setMediationModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {counterModal && (
        <div style={{ ...S.modal, alignItems: "center" }} onClick={() => { setCounterModal(null); setCounterDates(["","",""]); }}>
          <div style={{ ...S.modalBox(false), width: "100%", maxWidth: 460, borderRadius: 18 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: fontCond, fontSize: 20, fontWeight: 900, color: C.text, marginBottom: 4 }}>📅 Propor Novas Datas</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>O desafiante escolherá uma das datas que você propor.</div>
            {counterDates.map((d, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <label style={S.label}>Opção {i+1}</label>
                <RoundTimePicker value={d} onChange={v => { const nd = [...counterDates]; nd[i] = v; setCounterDates(nd); }} style={{ ...S.input, marginBottom: 0 }} allChallenges={allChallenges} otherDates={counterDates.filter((_, j) => j !== i)} blockedDates={state.blockedDates}/>
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={{ ...S.btn(C.accent), flex: 1 }} onClick={counterProposeDates}>Enviar</button>
              <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => { setCounterModal(null); setCounterDates(["","",""]); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {profileModal && me && (
        <div style={{ ...S.modal, alignItems: "center" }} onClick={() => setProfileModal(false)}>
          <div style={{ ...S.modalBox(false), width: "100%", maxWidth: 460, borderRadius: 18 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: fontCond, fontSize: 20, fontWeight: 900, color: C.text, marginBottom: 20 }}>✏️ Editar Perfil</div>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid " + C.border }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", overflow: "hidden", border: "3px solid " + primaryColor, flexShrink: 0 }}>
                {me.photo ? <img src={me.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/> : <Avatar player={me} size={64}/>}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text, marginBottom: 6 }}>Foto de perfil</div>
                <label style={{ ...S.btn(C.muted, true), padding: "7px 14px", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {photoUploading ? "Enviando…" : "Alterar foto"}
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={uploadPhoto}/>
                </label>
              </div>
            </div>
            <label style={S.label}>Email</label>
            <input style={S.input} type="email" placeholder="seu@email.com" value={profileEmail} onChange={e => setProfileEmail(e.target.value)} autoFocus/>
            <label style={S.label}>Telefone</label>
            <input style={S.input} type="tel" placeholder="(11) 99999-9999" value={profilePhone} onChange={e => setProfilePhone(e.target.value)}/>
            <div style={{ borderTop: "1px solid " + C.border, marginTop: 16, paddingTop: 16 }}>
              <div style={{ fontFamily: fontCond, fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 12 }}>🔑 Alterar Senha</div>
              <input style={S.input} type="password" value={oldPass} onChange={e => setOldPass(e.target.value)} placeholder="Senha atual"/>
              <input style={S.input} type="password" value={newPass1} onChange={e => setNewPass1(e.target.value)} placeholder="Nova senha"/>
              <input style={S.input} type="password" value={newPass2} onChange={e => setNewPass2(e.target.value)} placeholder="Confirmar nova senha"/>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={{ ...S.btn(C.accent), flex: 1 }} onClick={async () => { await saveProfile(); if (oldPass || newPass1 || newPass2) changePassword(); }}>Salvar</button>
              <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => { setProfileModal(false); setOldPass(""); setNewPass1(""); setNewPass2(""); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {passModal && (
        <div style={{ ...S.modal, alignItems: "center" }} onClick={() => { setPassModal(false); setOldPass(""); setNewPass1(""); setNewPass2(""); }}>
          <div style={S.modalBox(isMobile)} onClick={e => e.stopPropagation()}>
            {isMobile && <div style={{ width: 40, height: 4, background: C.border, borderRadius: 2, margin: "0 auto 20px" }}/>}
            <div style={{ fontFamily: fontCond, fontSize: 20, fontWeight: 900, color: C.text, marginBottom: 20 }}>🔑 Alterar Senha</div>
            <label style={S.label}>Senha atual</label>
            <input style={S.input} type="password" value={oldPass} onChange={e => setOldPass(e.target.value)} placeholder="••••••••" autoFocus/>
            <label style={S.label}>Nova senha</label>
            <input style={S.input} type="password" value={newPass1} onChange={e => setNewPass1(e.target.value)} placeholder="••••••••"/>
            <label style={S.label}>Confirmar nova senha</label>
            <input style={S.input} type="password" value={newPass2} onChange={e => setNewPass2(e.target.value)} placeholder="••••••••"/>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button style={{ ...S.btn(), flex: 1 }} onClick={changePassword}>Salvar</button>
              <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => { setPassModal(false); setOldPass(""); setNewPass1(""); setNewPass2(""); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {statsModal && (() => {
        const p = state.players.find(x => x.id === statsModal.id) || statsModal;
        const st = getPlayerStats(p.name, state.results);
        const pr = state.results.filter(r => r.challengerName === p.name || r.challengedName === p.name);
        return (
          <div style={{ ...S.modal, alignItems: "center" }} onClick={() => setStatsModal(null)}>
            <div style={{ ...S.modalBox(false), width: "100%", maxWidth: 460, borderRadius: 18 }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", overflow: "hidden", border: "3px solid " + primaryColor, flexShrink: 0 }}>
                  {p.photo ? <img src={p.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/> : <Avatar player={p} size={64}/>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: fontCond, fontWeight: 900, fontSize: 26, color: primaryColor, lineHeight: 1 }}>#{p.position}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "2px 0 4px" }}>
                    <span style={{ fontFamily: fontCond, fontWeight: 700, fontSize: 17, color: C.text }}>{p.name}</span>
                    {(() => {
                      const bl = getBlockStatus(p), cs = getChallengeStatus(p, state.challenges);
                      if (bl === "inativo") return <span style={S.badge("#666666")}>Inativo</span>;
                      if (cs === "desafiado" || cs === "ambos" || bl === "bloqueado") return <span style={S.badge("#e53e3e")}>Indisponível</span>;
                      return <span style={S.badge(C.accent)}>Disponível</span>;
                    })()}
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 5 }}>
                    {p.phone && (
                      <a href={`https://wa.me/55${p.phone.replace(/\D/g,"")}`} target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#1a7a3c" }}>{p.phone}</span>
                      </a>
                    )}
                    {p.email && (
                      <a href={`mailto:${p.email}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>
                        <span style={{ fontSize: 11, fontWeight: 600, color: primaryColor, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.email}</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                {[{label:"Vitórias",value:st.wins,color:C.success},{label:"Derrotas",value:st.losses,color:"#e53e3e"},{label:"Taxa",value:st.winRate+"%",color:C.accent,sub:st.total+" jogos"}].map(({label,value,color,sub}) => (
                  <div key={label} style={{ flex: 1, background: C.cardAlt, border: "1px solid " + C.border, borderRadius: 10, padding: "8px 10px", textAlign: "center" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, letterSpacing: .5, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
                    <div style={{ fontFamily: fontCond, fontWeight: 900, fontSize: 18, color, lineHeight: 1 }}>{value}</div>
                    {sub && <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{sub}</div>}
                  </div>
                ))}
              </div>
              {pr.length > 0 && (<>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>Últimas partidas</div>
                {[...pr].reverse().slice(0, 5).map((r, i) => {
                  const isChr = r.challengerName === p.name;
                  const won = (isChr && (r.winner === "challenger" || r.type === "wo-challenged")) || (!isChr && (r.winner === "challenged" || r.type === "wo-challenger"));
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                      {(() => {
                        const isWo = r.type === "wo-challenged" || r.type === "wo-challenger";
                        const label = isWo ? "W.O." : (won ? "V" : "D");
                        return <span style={{ ...S.badge(won ? C.success : "#e53e3e"), minWidth: 28, textAlign: "center", flexShrink: 0, fontSize: 10 }}>{label}</span>;
                      })()}
                      <span style={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>vs {isChr ? r.challengedName : r.challengerName}</span>
                      {r.score && <span style={{ fontFamily: fontCond, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{r.score}</span>}
                      <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{new Date(r.date).toLocaleDateString("pt-BR")}</span>
                    </div>
                  );
                })}
              </>)}
              <button style={{ ...S.btn(C.muted, true), width: "100%", marginTop: 16 }} onClick={() => setStatsModal(null)}>Fechar</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
